import { z } from '@faceup/validation'
import { YES_NO_OPTION_COUNT } from '../constants'
import { FormItemType } from '../enums'

/**
 * Wire-level schemas for abstract form items, one variant per {@link FormItemType}.
 *
 * Items are language-resolved: endpoints accept a requested language and return plain
 * `label`/`hint`/option `label` strings (falling back to the form's default language),
 * mirroring how the public GraphQL form queries resolve translations server-side.
 * Endpoints compose the variants they support into a discriminated union
 * (see `publicDisclosureFormItemSchema`) instead of redefining item shapes.
 *
 * `abstractFormItemSchema` unions all variants and is the single source of truth for the
 * resolved form item shape — the `AFForm` component's item types are inferred from it.
 */
export const baseFormItemSchema = z.object({
  id: z.string(),
  order: z.number(),
  isRequired: z.boolean(),
  label: z.string(),
  hint: z.string().nullable(),
})

export const formItemOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
})

export const simpleTextFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.SimpleText),
})

export const dateFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Date),
})

export const numberFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Number),
})

export const emailFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Email),
})

export const phoneNumberFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.PhoneNumber),
})

export const multilineTextFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.MultilineText),
  maxLength: z.number().nullable(),
})

export const selectFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Select),
  hasOtherOption: z.boolean(),
  options: z.array(formItemOptionSchema),
})

export const multiSelectFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.MultiSelect),
  hasOtherOption: z.boolean(),
  options: z.array(formItemOptionSchema),
  minResponses: z.number().nullable(),
  maxResponses: z.number().nullable(),
})

export const yesNoFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.YesNo),
  options: z.array(formItemOptionSchema).length(YES_NO_OPTION_COUNT),
})

export const checkboxFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Checkbox),
})

export const senderNameFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.SenderName),
})

export const classroomFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Classroom),
})

export const moreInformationFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.MoreInformation),
})

export const fileFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.File),
})

export const categoryFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Category),
  options: z.array(formItemOptionSchema),
})

export const organizationalUnitFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.OrganizationalUnit),
  options: z.array(formItemOptionSchema),
})

export const ratingFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Rating),
  options: z.array(formItemOptionSchema),
})

export const scaleFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Scale),
  options: z.array(formItemOptionSchema),
  startLabel: z.string().nullable(),
  endLabel: z.string().nullable(),
})

export const matrixFormItemSchema = baseFormItemSchema.extend({
  type: z.literal(FormItemType.Matrix),
  questions: z.array(z.object({ id: z.string() })),
})

export const abstractFormItemSchema = z.discriminatedUnion('type', [
  simpleTextFormItemSchema,
  dateFormItemSchema,
  numberFormItemSchema,
  emailFormItemSchema,
  phoneNumberFormItemSchema,
  multilineTextFormItemSchema,
  selectFormItemSchema,
  multiSelectFormItemSchema,
  yesNoFormItemSchema,
  checkboxFormItemSchema,
  senderNameFormItemSchema,
  classroomFormItemSchema,
  moreInformationFormItemSchema,
  fileFormItemSchema,
  categoryFormItemSchema,
  organizationalUnitFormItemSchema,
  ratingFormItemSchema,
  scaleFormItemSchema,
  matrixFormItemSchema,
])

export type AbstractFormItem = z.infer<typeof abstractFormItemSchema>
export type AbstractFormItemOption = z.infer<typeof formItemOptionSchema>
